import React, { useRef } from 'react';
import { motion } from 'framer-motion';
import { MessageSquare, Activity, Edit3, Calendar, BarChart2, Lightbulb, Lock, TrendingUp, ChevronLeft, ChevronRight } from 'lucide-react';
export const FeaturesSection = () => {
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const scroll = (direction: 'left' | 'right') => {
    if (scrollContainerRef.current) {
      const scrollAmount = direction === 'left' ? -320 : 320;
      scrollContainerRef.current.scrollBy({
        left: scrollAmount,
        behavior: 'smooth'
      });
    }
  };
  const features = [{
    icon: <MessageSquare size={20} data-id="element-2281" />,
    title: 'AI Chat Analysis',
    description: 'Deep contextual analysis of your conversations to identify underlying emotional tones.'
  }, {
    icon: <Activity size={20} data-id="element-2282" />,
    title: 'Emotional Pattern Detection',
    description: 'Recognize recurring behaviors like gaslighting, guilt-tripping, or healthy support.'
  }, {
    icon: <Edit3 size={20} data-id="element-2283" />,
    title: 'Message Rewriter',
    description: 'Transform emotionally charged drafts into constructive, boundary-setting responses.'
  }, {
    icon: <Calendar size={20} data-id="element-2284" />,
    title: 'Weekly Relationship Reports',
    description: 'Track the health of your communication over time with automated weekly summaries.'
  }, {
    icon: <BarChart2 size={20} data-id="element-2285" />,
    title: 'Toxicity Scoring',
    description: 'Objective metrics to help you gauge the overall health of your interactions.'
  }, {
    icon: <Lightbulb size={20} data-id="element-2286" />,
    title: 'Communication Insights',
    description: 'Actionable advice on how to navigate difficult conversations and set boundaries.'
  }, {
    icon: <Lock size={20} data-id="element-2287" />,
    title: 'Privacy & Encryption',
    description: 'Your data is secured with AES-256 encryption and processed with strict confidentiality.'
  }, {
    icon: <TrendingUp size={20} data-id="element-2288" />,
    title: 'Relationship Trend Tracking',
    description: 'Visualize improvements or declines in communication quality over months or years.'
  }];
  return <section className="py-24 bg-[#050505] relative overflow-hidden border-t border-gray-900" id="features" data-id="element-2289">
      <div className="max-w-6xl mx-auto px-6 relative z-10" data-id="element-2290">
        <div className="text-center mb-16" data-id="element-2291">
          <h2 className="text-3xl md:text-5xl font-black text-white mb-6 uppercase tracking-tight" data-id="element-2292">
            System <span className="text-[#FFD700]" data-id="element-2293">Capabilities</span>
          </h2>
          <p className="text-gray-400 text-lg max-w-2xl mx-auto font-mono" data-id="element-2294">
            A complete suite of forensic tools designed to bring objectivity and
            insight to your relationships.
          </p>
        </div>

        {/* Carousel Controls */}
        <div className="flex justify-end gap-3 mb-6" data-id="element-2295">
          <button onClick={() => scroll('left')} className="w-10 h-10 rounded-full border border-gray-800 bg-[#0A0A0A] flex items-center justify-center text-gray-400 hover:text-white hover:border-[#FFD700] transition-colors" aria-label="Previous features" data-id="element-2296">
            <ChevronLeft size={20} data-id="element-2297" />
          </button>
          <button onClick={() => scroll('right')} className="w-10 h-10 rounded-full border border-gray-800 bg-[#0A0A0A] flex items-center justify-center text-gray-400 hover:text-white hover:border-[#FFD700] transition-colors" aria-label="Next features" data-id="element-2298">
            <ChevronRight size={20} data-id="element-2299" />
          </button>
        </div>

        {/* Features Carousel */}
        <div ref={scrollContainerRef} className="flex overflow-x-auto gap-4 pb-8 snap-x snap-mandatory hide-scrollbar" style={{
        scrollbarWidth: 'none',
        msOverflowStyle: 'none'
      }} data-id="element-2300">
          {features.map((feature, index) => <motion.div key={index} initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          delay: index * 0.05,
          duration: 0.5
        }} className="bg-[#0A0A0A] border border-gray-800 rounded-2xl p-6 hover:border-[#FFD700]/50 transition-colors min-w-[280px] md:min-w-[320px] snap-start flex-shrink-0 group relative overflow-hidden" data-id="element-2301">
              {/* Hover Scan Line */}
              <div className="absolute bottom-0 left-0 w-full h-[2px] bg-[#FFD700] transform scale-x-0 group-hover:scale-x-100 transition-transform duration-300 origin-left" data-id="element-2302" />

              <div className="w-10 h-10 bg-white/5 rounded-lg flex items-center justify-center text-gray-300 mb-4 border border-white/10 group-hover:text-[#FFD700] group-hover:border-[#FFD700]/30 transition-colors" data-id="element-2303">
                {feature.icon}
              </div>
              <h3 className="text-base font-semibold text-white mb-2 uppercase tracking-wide group-hover:text-[#FFD700] transition-colors" data-id="element-2304">
                {feature.title}
              </h3>
              <p className="text-gray-400 text-sm leading-relaxed font-mono" data-id="element-2305">
                {feature.description}
              </p>
            </motion.div>)}
        </div>
      </div>
      <style dangerouslySetInnerHTML={{
      __html: `
        .hide-scrollbar::-webkit-scrollbar {
          display: none;
        }
      `
    }} data-id="element-2306" />
    </section>;
};